import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  ButtonProps,
  Flex,
  FormControl,
  FormLabel,
  Switch,
  SwitchProps,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {FC, useMemo} from 'react';
import {cssVarNamespace} from '../CookieBannerDefinition';
import {
  contentTranslations,
  privacyPreferences,
  type CookieCategoryItem,
  type SupportedLocaleType,
} from '../content';

interface CookieCategoryProps {
  theme: 'dark' | 'light';
  item: CookieCategoryItem;
  index: number;
  accentColor: string;
  textColor: string;
  userLocale: SupportedLocaleType;
  onSwitchChange: (index: number, item: CookieCategoryItem) => void;
}

export const CookieCategory: FC<CookieCategoryProps> = ({
  theme,
  item,
  index,
  accentColor,
  textColor,
  userLocale,
  onSwitchChange,
}) => {
  const itemBackgroundColor = `var(--${cssVarNamespace}ItemBackgroundColor, ${
    theme === 'dark' ? '#000' : '#cfcfcf'
  })`;

  const localizedItem = privacyPreferences[userLocale].find(
    (pref) => item.key === pref.key
  );
  const cookies = localizedItem?.cookies ?? item.cookies;

  const accordionButtonStyles: ButtonProps = useMemo(
    () => ({
      backgroundColor: itemBackgroundColor,
      borderRadius: '16px',
      gap: '0.5rem',
      px: 3,
      _hover: {
        backgroundColor: theme === 'dark' ? '#0e0e0e' : '#e8e8e8',
      },
    }),
    [itemBackgroundColor, theme]
  );

  const switchStyles: SwitchProps = useMemo(
    () => ({
      size: 'md',
      sx: {
        '.chakra-switch__track': {
          '--switch-bg':
            theme === 'dark'
              ? 'rgba(255, 255, 255, 0.5)'
              : 'rgba(0, 0, 0, 0.5);',
        },
        '.chakra-switch__track[data-checked]': {
          '--switch-bg': `${accentColor}`,
        },
        ".chakra-switch__track[aria-checked='true']": {
          '--switch-bg': `${accentColor}`,
        },
      },
    }),
    [theme, accentColor]
  );
  return (
    <AccordionItem border="none">
      <h2>
        <AccordionButton {...accordionButtonStyles}>
          <AccordionIcon color={textColor} />
          <Box color={textColor} as="span" flex="1" textAlign="left">
            {localizedItem?.title ?? item.title}
          </Box>
          <FormControl as={Flex} alignItems="center" justifyContent="flex-end">
            {item.status.key === 'alwaysEnabled' ? (
              <Text
                fontStyle={'italic'}
                color={textColor}
                opacity={0.6}
                mr="0.5rem"
              >
                {localizedItem?.status?.title ?? item.status.title}
              </Text>
            ) : (
              <>
                <FormLabel
                  htmlFor="email-alerts"
                  mb="0"
                  color={textColor}
                  opacity={0.6}
                >
                  {localizedItem?.status?.title ?? item.status.title}
                </FormLabel>
                <Switch
                  {...switchStyles}
                  isChecked={item.status.key === 'enabled'}
                  data-testid={`checkbox-${item.key}`}
                  onChange={() => {
                    onSwitchChange(index, item);
                  }}
                />
              </>
            )}
          </FormControl>
        </AccordionButton>
      </h2>
      <AccordionPanel color={textColor} pb={4} px="1rem">
        {localizedItem?.description ?? item.description}
        <Box overflowX="auto">
          {cookies && cookies.length > 0 && (
            <Table
              variant="simple"
              mt={4}
              sx={{
                'td, td pre': {
                  borderColor:
                    theme === 'dark'
                      ? 'rgba(255,255,255,0.01)'
                      : 'rgba(0,0,0,0.01)',
                  fontSize: '0.875rem',
                },
                th: {
                  borderColor:
                    theme === 'dark'
                      ? 'rgba(255,255,255,0.01)'
                      : 'rgba(0,0,0,0.01)',
                },
              }}
            >
              <Thead>
                <Tr
                  bg={
                    theme === 'dark'
                      ? 'rgba(255,255,255,0.15)'
                      : 'rgba(0,0,0,0.15)'
                  }
                >
                  <Th color={textColor}>
                    {contentTranslations[userLocale].nameHeader}
                  </Th>
                  <Th color={textColor}>
                    {contentTranslations[userLocale].durationHeader}
                  </Th>
                  <Th color={textColor}>
                    {contentTranslations[userLocale].descriptionHeader}
                  </Th>
                </Tr>
              </Thead>
              <Tbody
                bg={
                  theme === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.05)'
                }
              >
                {cookies.map((cookie, index) => (
                  <Tr key={index}>
                    <Td>
                      <pre>{cookie.name}</pre>
                    </Td>
                    <Td>{cookie.duration}</Td>
                    <Td>{cookie.description}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </AccordionPanel>
    </AccordionItem>
  );
};
